// The placement values Meta accepts on ad-set create/update, established by a `validate_only`
// probe against Graph v24 on 2026-08-08. Offer and send only these.
//
// Two values Meta used to accept are deliberately absent: `facebook_positions: video_feeds`
// ("deprecated for this API version and cannot be selected" — Facebook Reels replaced it) and
// `messenger_positions: sponsored_messages` ("MESSENGER_THREAD placement is no longer
// available"). Ad sets created before v24 still read back with them, so the stored-spec types
// below stay wide while the sendable arrays do not.
export const sendablePlacementPositions = {
  publisher_platforms: [
    'facebook',
    'instagram',
    'messenger',
    'audience_network',
  ] as const,
  // prettier-ignore
  facebook_positions: ['feed', 'right_hand_column', 'instant_article', 'marketplace', 'story', 'search', 'facebook_reels', 'facebook_reels_overlay', 'profile_feed', 'biz_disco_feed', 'groups_feed'] as const,
  // prettier-ignore
  // Instagram's Reels value is plain `reels`; Facebook's is `facebook_reels`. The asymmetry is Meta's.
  instagram_positions: ['stream', 'story', 'explore', 'explore_home', 'reels', 'profile_feed', 'ig_search', 'profile_reels', 'reels_overlay'] as const,
  audience_network_positions: [
    'classic',
    'instream_video',
    'rewarded_video',
  ] as const,
  messenger_positions: ['messenger_home', 'story'] as const,
};

export const placementPositionKeys = [
  'facebook_positions',
  'instagram_positions',
  'audience_network_positions',
  'messenger_positions',
] as const;
export type PlacementPositionKey = typeof placementPositionKeys[number];

export const platformByPositionKey: {
  [key in PlacementPositionKey]: PublisherPlatforms;
} = {
  facebook_positions: 'facebook',
  instagram_positions: 'instagram',
  audience_network_positions: 'audience_network',
  messenger_positions: 'messenger',
};

export type PublisherPlatforms = typeof sendablePlacementPositions['publisher_platforms'][number];
export type SendableFacebookPosition = typeof sendablePlacementPositions['facebook_positions'][number];
export type InstagramPositions = typeof sendablePlacementPositions['instagram_positions'][number];
export type AudienceNetworkPositions = typeof sendablePlacementPositions['audience_network_positions'][number];
export type SendableMessengerPosition = typeof sendablePlacementPositions['messenger_positions'][number];

// Wider than what may be sent — these two describe specs read back from Meta. See the note above.
export type FacebookPositions = SendableFacebookPosition | 'video_feeds';
export type MessengerPositions =
  | SendableMessengerPosition
  | 'sponsored_messages';

/**
 * A manual position selection, per platform. A platform's key is **absent** when every one of
 * its positions is selected, because that is how Meta reads an absent key — which is what stops
 * a placement Meta adds later from being silently excluded by our stale list. An empty array is
 * never a valid value: Meta accepts it and then ignores it.
 */
export type PlacementPositions = {
  facebook_positions?: SendableFacebookPosition[];
  instagram_positions?: InstagramPositions[];
  audience_network_positions?: AudienceNetworkPositions[];
  messenger_positions?: SendableMessengerPosition[];
};

export type PlatformPlacementTargeting = {
  publisher_platforms: PublisherPlatforms[];
  facebook_positions: FacebookPositions[];
  instagram_positions: InstagramPositions[];
  audience_network_positions: AudienceNetworkPositions[];
  messenger_positions: MessengerPositions[];
};
